import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useProperty } from '../context/PropertyContext';
import { useLanguage } from '../context/LanguageContext';
import PropertyCard from '../components/common/PropertyCard';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Pencil, Trash2, CheckCircle } from 'lucide-react';

const MyProperties: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { properties } = useProperty();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [myProps, setMyProps] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  // Redirect to login if not authenticated (but only after auth is loaded)
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return; // Don't fetch if not authenticated
    
    const fetchMine = async () => {
      try {
        setLoading(true);
        const { items } = await apiService.getMyListings();
        const mapped = items.map((l: any) => ({
          id: l.id,
          title: l.title,
          description: l.description ?? '',
          price: l.price ?? 0,
          address: {
            street: l.address ?? '',
            city: l.city ?? '',
            state: '',
            zipCode: l.zip_code ?? l.zipCode ?? '',
            country: l.country ?? ''
          },
          images: (l.images ?? []).map((img: any) => img.public_url ?? img.publicUrl).filter(Boolean),
          type: 'apartment',
          status: (l.purpose === 'RENT' ? 'rent' : 'sale'),
          features: {
            bedrooms: l.rooms ?? 0,
            bathrooms: l.bathrooms ?? 0,
            area: Number(l.area_m2 ?? l.areaM2 ?? 0),
            garage: false,
            yearBuilt: new Date(l.created_at ?? l.createdAt ?? new Date()).getFullYear()
          },
          amenities: [],
          contactInfo: { name: '', phone: '', email: '' },
          owner: l.owner_user_id ?? l.ownerUserId ?? '',
          createdAt: l.created_at ?? l.createdAt ?? new Date().toISOString(),
          updatedAt: l.updated_at ?? l.updatedAt ?? new Date().toISOString()
        }));
        setMyProps(mapped);
      } catch (e: any) {
        setError(e?.message || t('home.errors.failedToLoadListings'));
      } finally {
        setLoading(false);
      }
    };
    fetchMine();
  }, [user, t]);

  const handlePropertyClick = (id: string) => {
    navigate(`/property/${id}`);
  };

  const handleEdit = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    navigate(`/edit-property/${id}`);
  };

  const handleDelete = async (id: string) => {
    try {
      await apiService.deleteListing(id);
      // Refresh the list
      const { items } = await apiService.getMyListings();
      const mapped = items.map((l: any) => ({
        id: l.id,
        title: l.title,
        description: l.description ?? '',
        price: l.price ?? 0,
        address: {
          street: l.address ?? '',
          city: l.city ?? '',
          state: '',
          zipCode: l.zip_code ?? l.zipCode ?? '',
          country: l.country ?? ''
        },
        images: (l.images ?? []).map((img: any) => img.public_url ?? img.publicUrl).filter(Boolean),
        type: 'apartment',
        status: (l.purpose === 'RENT' ? 'rent' : 'sale'),
        features: {
          bedrooms: l.rooms ?? 0,
          bathrooms: l.bathrooms ?? 0,
          area: Number(l.area_m2 ?? l.areaM2 ?? 0),
          garage: false,
          yearBuilt: new Date(l.created_at ?? l.createdAt ?? new Date()).getFullYear()
        },
        amenities: [],
        contactInfo: { name: '', phone: '', email: '' },
        owner: l.owner_user_id ?? l.ownerUserId ?? '',
        createdAt: l.created_at ?? l.createdAt ?? new Date().toISOString(),
        updatedAt: l.updated_at ?? l.updatedAt ?? new Date().toISOString()
      }));
      setMyProps(mapped);
      setShowDeleteDialog(null);
      
      // Show success toast
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    } catch (err: any) {
      alert(t('home.errors.failedToDelete') + ': ' + (err?.message || t('home.errors.unknownError')));
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // If not authenticated and not loading, the useEffect will redirect
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">
          {t('common.myProperties')}
        </h1>
        {loading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        )}
        {error && <p className="text-red-600">{error}</p>}
        {!loading && !error && myProps.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">{t('profile.noProperties')}</p>
            <button
              onClick={() => navigate('/add-property')}
              className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              {t('common.addProperty')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {myProps.map((property) => (
              <div key={property.id} className="relative">
                <PropertyCard
                  property={property}
                  onClick={() => handlePropertyClick(property.id)}
                />
                <div className="absolute top-4 right-4 flex gap-2">
                  <button
                    onClick={(e) => handleEdit(e, property.id)}
                    className="p-2 bg-white rounded-full shadow-md hover:bg-primary-50 transition-colors"
                    title={t('common.edit')}
                  >
                    <Pencil size={18} className="text-primary-500" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteDialog(property.id);
                    }}
                    className="p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
                    title={t('common.delete')}
                  >
                    <Trash2 size={18} className="text-red-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('profile.deleteConfirmation')}</h3>
            <p className="text-gray-600 mb-6">
              {t('profile.deleteMessage')}
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteDialog(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleDelete(showDeleteDialog)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {showSuccessToast && (
        <div className="fixed top-8 right-8 z-50 animate-slide-in">
          <div className="bg-primary-500 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
            <CheckCircle className="w-6 h-6" />
            <div>
              <p className="font-semibold">{t('profile.deleteSuccess')}</p>
              <p className="text-sm text-primary-100">{t('profile.listUpdated')}</p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MyProperties;
